import React, { useState, useEffect, useCallback } from 'react';
import Alert from '../Assets/Alerts.js';
import PropTypes from 'prop-types';
import { Multiselect } from 'multiselect-react-dropdown';
import MultiSelect from '../Assets/MultiSelect.js';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addValue, removeValue } from '../../react-redux/Slicer/GenericIdsSlice.js';


const FormModal = ({ showModal, setShowModal, title, fields, handleSubmit, loader, previousAttachments = [], onDeleteAttachment, OngoingEvents }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ type: '', message: '' });
    const [dynamicRequirements, setDynamicRequirements] = useState({});




    useEffect(() => {
        dispatch(removeValue({ key: 'template_id' }));
        dispatch(removeValue({ key: 'breadcrumb' }));
        const modalElement = document.getElementById('staticBackdrop');
        if (showModal) {
            // Remove aria-hidden when modal is shown
            if (modalElement) {
                modalElement.removeAttribute('aria-hidden');
            }
        } else if (modalElement) {
            modalElement.setAttribute('aria-hidden', 'true');

        }
    }, [showModal]);

    FormModal.propTypes = {
        showModal: PropTypes.bool.isRequired,
        setShowModal: PropTypes.func.isRequired,
        title: PropTypes.string.isRequired,
        fields: PropTypes.arrayOf(PropTypes.object).isRequired,
        handleSubmit: PropTypes.func.isRequired,
        loader: PropTypes.bool,
        previousAttachments: PropTypes.array,
        OngoingEvents: PropTypes.array,
        onDeleteAttachment: PropTypes.func
    };

    useEffect(() => {
        if (showModal && fields) {
            const initialFormData = {};
            fields.forEach(field => {
                initialFormData[field.name] = field.value !== undefined ? field.value : '';
            });
            setFormData(initialFormData);

            // Initialize dynamic requirements for sub_activity_type handling
            if (fields.some(f => f.name === 'sub_activity_type')) {
                const isDynamicRequiredForm = true;
                if (isDynamicRequiredForm) {
                    // Will be updated when sub_activity_type changes
                    setDynamicRequirements({});
                }
            }
        }
    }, [showModal, fields]);

    // Handle sub_activity_type changes to dynamically update field requirements
    useEffect(() => {
        if (formData.sub_activity_type) {
            // For Inspection type (value "1"), make certain fields optional (required: false)
            // For Quantitative type (value "2"), make certain fields required (required: true)
            const isInspectionType = formData.sub_activity_type === "1";
            setDynamicRequirements({
                planned_quantity: !isInspectionType,  // true for Quantitative, false for Inspection
                uom: !isInspectionType,               // true for Quantitative, false for Inspection
                logical_operator: !isInspectionType,  // true for Quantitative, false for Inspection
            });
        } else {
            // Reset to default when no sub_activity_type is selected
            setDynamicRequirements({
                planned_quantity: true,
                uom: true,
                logical_operator: true,
            });
        }
    }, [formData.sub_activity_type]);

    useEffect(() => {
        if (alert.message) {
            const timer = setTimeout(() => {
                setAlert({ type: '', message: '' });
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [alert]);

    // Helper for file input change
    const handleFileChange = (e, field) => {
        const { name, files } = e.target;
        const acceptList = field?.accept?.split(',').map(item => item.trim().toLowerCase()) || [];
        const isAccepted = (file) =>
            acceptList.length === 0 ||
            acceptList.some(
                (acc) =>
                    file.name.toLowerCase().endsWith(acc) ||
                    file.type.toLowerCase() === acc
            );

        if (field?.multiple) {
            const fileArr = Array.from(files).filter(isAccepted);
            if (fileArr.length < files.length) {
                setAlert({
                    type: 'error',
                    message: `Some files are not accepted: ${Array.from(files)
                        .filter((f) => !isAccepted(f))
                        .map((f) => f.name)
                        .join(', ')}`,
                });
            } else {
                setFormData((prev) => ({ ...prev, [name]: fileArr }));
                if (typeof field?.onChange === 'function') {
                    field.onChange(fileArr);
                }
            }
        } else {
            const file = files[0];
            if (file && !isAccepted(file)) {
                setAlert({
                    type: 'error',
                    message: `File type not accepted: ${file.name}`,
                });
            } else {
                setFormData((prev) => ({ ...prev, [name]: file }));
                if (typeof field?.onChange === 'function') {
                    field.onChange(file);
                }
            }
        }
    };

    // Helper for value input change
    const handleValueChange = (e, field) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (field && typeof field.onBlur === 'function') {
            field.onBlur(value);
        }
        if (field && typeof field.onChange === 'function') {
            field.onChange(value);
        }
    };

    const handleChange = (e) => {
        const { name, type } = e.target;
        const field = fields.find((f) => f.name === name);
        if (type === 'file') {
            handleFileChange(e, field);
        } else {
            handleValueChange(e, field);
        }
    };



    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // Special handling for cancel_dates: convert comma-separated string to array
            let submitData = { ...formData };
            if (submitData.cancel_dates && typeof submitData.cancel_dates === 'string') {
                submitData.cancel_dates = submitData.cancel_dates
                    .split(',')
                    .map(date => date.trim())
                    .filter(date => date);
            }
            const response = await handleSubmit(submitData);
            if (response) {
                return response;
            }

        } catch (error) {
            console.error(error);
            setAlert({ type: 'error', message: error.message });
        }
        setLoading(false);
    };

    // Helper to determine if a field should be shown based on criticality
    const shouldShowField = useCallback((field) => {
        // Hide sub activity and planned quantity if Non-Critical
        if (
            ['sub_activity_type', 'planned_quantity', 'logical_operator'].includes(field?.name)
        ) {
            // criticality can be string or number, treat '0' and 0 as Non-Critical
            if (formData.criticality === '0' || formData.criticality === 0) {
                return false;
            }
        }
        return true;
    }, [formData.criticality]); // Memoized with useCallback

    useEffect(() => {
        shouldShowField(fields);
    }, [formData.criticality, fields, shouldShowField]); // Now safe to include shouldShowField


    if (!showModal) return null;
    return (
        <div>
            {loader && <div style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(1px)', zIndex: 9999 }}>
                <img src="/images/plot_loader_gif.gif" alt="processing....." style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                <h4 style={{ position: 'absolute', top: '60%', left: '50%', transform: 'translate(-50%, -50%)', color: '#ccc', marginTop: '10px', marginLeft: '20px' }}>Processing......</h4>
            </div>}
            {alert.message && <Alert type={alert.type} message={alert.message} />}
            <div className="modal fade show" id="staticBackdrop" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" aria-labelledby="staticBackdropLabel" style={{ display: 'block', background: 'rgba(0, 0, 0, 0.7)', transition: 'opacity 0.15s linear', opacity: showModal ? 1 : 0 }}>
                <div className="modal-dialog modal-dialog-centered ">
                    <div className="modal-content">
                        <div className={`modal-header ${title === 'Ongoing Events List' ? 'danger-1' : 'success-1'}`}>
                            <h1 className="modal-title fs-5" id="staticBackdropLabel">{title}</h1>

                        </div>
                        <div className="modal-body">
                            <form onSubmit={onSubmit}>
                                {fields?.filter(shouldShowField).map((field) => {
                                    if (field.type === 'hidden') {
                                        return (
                                            <input
                                                type="hidden"
                                                id={field.name}
                                                name={field.name}
                                                value={formData[field.name]}
                                                key={field.name}
                                            />
                                        );
                                    }
                                    let fieldContent;
                                    if (field.type === 'multiselect') {
                                        fieldContent = (
                                            <Multiselect
                                                options={field.options || []}
                                                selectedValues={formData[field.name] || []}
                                                onSelect={(selectedList) => {
                                                    setFormData({ ...formData, [field.name]: selectedList });
                                                    if (typeof field.onBlur === 'function') field.onBlur(selectedList);
                                                }}
                                                onRemove={(selectedList) => {
                                                    setFormData({ ...formData, [field.name]: selectedList });
                                                    if (typeof field.onBlur === 'function') field.onBlur(selectedList);
                                                }}
                                                displayValue={field.displayValue || 'label'}
                                                showCheckbox
                                                placeholder={field.placeholder || 'Select'}
                                                className="form-control form-control-sm p-0"
                                                style={{ minHeight: '38px' }}
                                                required={field.required}
                                            />
                                        );
                                    } else if (field.type === 'multiSelect') {
                                        fieldContent = (
                                            <MultiSelect
                                                options={field.options || []}
                                                value={field.value}
                                                onChange={(selectedList) => {
                                                    setFormData({ ...formData, [field.name]: selectedList });
                                                    if (typeof field.onChange === 'function') field.onChange(selectedList);
                                                    if (typeof field.onBlur === 'function') field.onBlur(selectedList);
                                                }}
                                                placeholder={field.placeholder || 'Select'}
                                                isMulti={true}
                                                required={field.required}
                                            />
                                        );
                                    }

                                    else if (field.type === 'select') {
                                        const isRequired = dynamicRequirements.hasOwnProperty(field.name)
                                            ? dynamicRequirements[field.name]
                                            : field.required;
                                        fieldContent = (
                                            <select
                                                className="form-select form-select-sm"
                                                id={field.name}
                                                name={field.name}
                                                value={formData[field.name] || ''}
                                                onChange={(e) => {
                                                    handleChange(e);
                                                    if (field.onChange) field.onChange(e.target.value);
                                                }}
                                                required={isRequired}
                                            >
                                                <option value="" disabled>{field.placeholder || 'Select'}</option>
                                                {(field.options || []).map((option) => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        );
                                    } else {
                                        const isRequired = dynamicRequirements.hasOwnProperty(field.name)
                                            ? dynamicRequirements[field.name]
                                            : field.required;
                                        fieldContent = (
                                            <input
                                                type={field.type || 'text'}
                                                className="form-control form-control-sm"
                                                id={field.name}
                                                name={field.name}
                                                value={field.type === 'file' ? undefined : formData[field.name]}
                                                onChange={handleChange}
                                                placeholder={field.placeholder}
                                                required={isRequired}
                                                disabled={field.disabled}
                                                min={field.min}
                                                max={field.max}
                                                readOnly={field.readOnly}
                                                multiple={field.type === 'file' && field.multiple}
                                                accept={field.accept || '*/*'}
                                                capture={field.capture || undefined}
                                            />
                                        );
                                    }
                                    const displayRequired = dynamicRequirements.hasOwnProperty(field.name)
                                        ? dynamicRequirements[field.name]
                                        : field.required;
                                    return (
                                        <div className="row mb-1" key={field.name}>
                                            <div className="form-label col-4 align-content-center">
                                                <label htmlFor={field.name}>{field.label}</label>
                                                {displayRequired && <span className='text-danger'>*</span>}
                                            </div>
                                            <div className="form-group col-8">
                                                {fieldContent}
                                                {/* Show media list under media input */}
                                            </div>
                                            {field.view && (
                                                <div className="form-label align-items-center mt-2">
                                                    <label htmlFor="" className='fw-600'>{field.view.label}</label>
                                                    <span className='text-muted '>{field.view.value} </span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* Always show media attachments if present */}
                                {Array.isArray(previousAttachments) && previousAttachments.length > 0 && (
                                    <div className='form-group col-12'>
                                        <div className="mt-2 border rounded bg-light p-2">
                                            <div className="fw-bold mb-1" style={{ fontSize: '0.95em' }}>List of {title}</div>
                                            <ul className="list-unstyled mb-0">
                                                {previousAttachments.map((media) => (
                                                    <li key={media?.ma_id} className="d-flex align-items-center justify-content-between border-bottom py-1 ">
                                                        <Link
                                                            to={media?.file_url}
                                                            className={`text-nowrap overflow-hidden ${media?.status === 1 ? 'success-2' : 'text-primary'}`}
                                                            target="_blank"
                                                            title="View Attachment"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                dispatch(addValue({ key: 'template_id', value: media?.state?.template_id }));
                                                                dispatch(addValue({ key: 'breadcrumb', value: media?.state?.breadcrumb }));
                                                            }}
                                                        >
                                                            {media.attachment_name}
                                                        </Link>
                                                        {onDeleteAttachment && <button type="button" className="btn btn-sm btn-outline-danger ms-3 px-2 py-0" title="Delete" onClick={() => onDeleteAttachment(media)}>
                                                            <i className="fa fa-times"></i>
                                                        </button>}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}



                                <div className="modal-footer">
                                    {handleSubmit && title !== 'Ongoing Events List' ? (<><button type="button" className="btn btn-secondary btn-sm" data-bs-dismiss="modal" onClick={() => setShowModal(false)}>Cancel</button>
                                        <button className="btn btn-success btn-sm" type="submit" disabled={loading}>Save</button></>) : <button type="button" className="btn btn-secondary btn-sm" data-bs-dismiss="modal" onClick={() => setShowModal(false)}>Close</button>}
                                </div>
                            </form>
                        </div></div>
                </div>
            </div>
        </div>
    );
};

export default FormModal;



